/**
 * The block editor: a rule built as blocks, with its sentence underneath
 * (roadmap E2, `docs/plans/argus.md` *Three doors*).
 *
 * Click-first. Every piece that can change is a pill or a word you click; a
 * small menu or field opens beside it. Groups take "+ condition". The sentence
 * under the blocks is the server's - `POST /api/argus/preview` - so there is
 * one renderer, the one whose words are stored with the version.
 *
 * The tree is edited through the pure functions in `argus-tree-edit.ts`; this
 * file only draws and routes clicks. Nothing here evaluates a rule (rule 3).
 */

import { html, nothing, svg, type SVGTemplateResult, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { directive, Directive, PartType, type ElementPart, type PartInfo } from 'lit/directive.js';
import { ref } from 'lit/directives/ref.js';
import { LightElement } from '../../lit-base.ts';
import {
  argusFormula,
  listArgusEntities,
  previewArgusRule,
  resolveArgusScope,
} from '../../api/endpoints.ts';
import type {
  ColumnInfo,
  ArgusEntity,
  ArgusPreview,
  ArgusRule,
  ArgusRuleChart,
  ArgusScopeSummary,
  ArgusTreeDocument,
  ArgusTreeNode,
  ArgusTreeScope,
} from '../../api/types.ts';
import { FUNCTION_WORDS, clause, durationWords, groupWords, logicKey } from './argus-rule-blocks.ts';
import '../../ui/number-formula-editor.ts';
import {
  assistantEditorEdit,
  openEditor,
  rules,
  saveRule,
  setCharts,
  setStatus,
  sources,
  type AssistantEditorEdit,
  type AssistantRuleDraft,
} from './argus-rules-store.ts';
import '../../ui/hold-switch.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo } from '../../state/store.ts';
import {
  AGGREGATES,
  COMPARISONS,
  DURATIONS,
  WINDOWS,
  addDefine,
  conditionText,
  duplicateDefine,
  blankDocument,
  editable,
  foldGate,
  formulaNames,
  formulaOf,
  getAt,
  groupItems,
  groupKind,
  inputFor,
  isNumberNode,
  negated,
  newParam,
  normalize,
  pushAt,
  putSignal,
  refsInUse,
  removeItem,
  setAt,
  setGroupCount,
  setGroupKind,
  signalKeys,
  simplification,
  simplifyAt,
  template,
  toggleNot,
  unchosenInputs,
  usesDefine,
  type BlockKind,
  type GroupKind,
  type Path,
} from './argus-tree-edit.ts';
import './argus-backtest-panel.ts';
import './argus-chart-dialog.ts';
import { DEFAULT_SCHEDULE, scheduleWords } from './argus-schedule-panel.ts';
import { userMessage } from '../../api/errors.ts';
import { dismissToast, showToast } from '../../ui/toast.ts';

const COMPARE_WORDS: Record<string, string> = {
  '>': 'above',
  '>=': 'at least',
  '<': 'below',
  '<=': 'at most',
  '==': 'equals',
  '!=': 'is not',
};

const AGGREGATE_WORDS: Record<string, string> = {
  avg_over: 'average',
  min_over: 'lowest',
  max_over: 'highest',
  p95_over: '95th percentile',
  sum_over: 'total',
  stddev_over: 'spread',
  change_over: 'change',
  missing_over: 'missing readings',
};

/** Arithmetic, read as words: the blocks say "DG1 plus DG2", not "DG1 + DG2". */
const ARITH_WORDS: Record<string, string> = { '+': 'plus', '-': 'minus', '*': 'times', '/': 'divided by' };
const ARITH_SIGNS: Record<string, string> = { '+': '+', '-': '−', '*': '×', '/': '÷' };
const COMPARE_SIGNS: Record<string, string> = { '>': '>', '>=': '≥', '<': '<', '<=': '≤', '==': '=', '!=': '≠' };
const FORMULA_WINDOWS: Record<string, string> = {
  avg_over: 'AVG', min_over: 'MIN', max_over: 'MAX', sum_over: 'SUM', p95_over: 'P95', stddev_over: 'STDDEV',
  change_over: 'CHANGE', missing_over: 'MISSING',
};

/** A value worked out from others - arithmetic or a function - and so drawn as a box of its own. */
function isComputed(node: unknown): boolean {
  const op = (node as { op?: string } | null)?.op;
  return typeof op === 'string' && (op in ARITH_WORDS || op in FUNCTION_WORDS);
}

/** A comparison with a computed side: drawn as a card with a formula line, not a stretched hexagon. */
export function isCard(node: unknown): boolean {
  const inner = negated(node as ArgusTreeNode)?.inner ?? node;
  const n = inner as { op?: string; args?: unknown[] } | null;
  return !!n?.op && n.op in COMPARE_WORDS && (isComputed(n.args?.[0]) || isComputed(n.args?.[1]));
}

/** The formula line under a card: `MAX(AC2, AC3) > AC1 + 0.2`. Only shown, never parsed. */
export function formulaText(node: unknown, doc: ArgusTreeDocument, inner = false): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (typeof node !== 'object' || node === null) return '?';
  if ('input' in node) return doc.inputs[(node as { input: string }).input]?.series || '?';
  if ('param' in node) return String(doc.params[(node as { param: string }).param]?.value ?? '?');
  if ('const' in node) return String((node as { const: number }).const);
  if ('ref' in node) return doc.define[(node as { ref: string }).ref]?.label ?? '?';
  const { op, args } = node as { op: string; args: unknown[] };
  if (op in COMPARE_SIGNS) return `${formulaText(args[0], doc)} ${COMPARE_SIGNS[op]} ${formulaText(args[1], doc)}`;
  if (op in ARITH_SIGNS) {
    const text = `${formulaText(args[0], doc, true)} ${ARITH_SIGNS[op]} ${formulaText(args[1], doc, true)}`;
    return inner ? `(${text})` : text;
  }
  if (op in FUNCTION_WORDS || op in FORMULA_WINDOWS) {
    const name = FORMULA_WINDOWS[op] ?? op.toUpperCase();
    return `${name}(${args.map((a) => formulaText(a, doc)).join(', ')})`;
  }
  return op;
}

/** What the ± beside a number offers, most useful first. */
const ARITH_CHOICES: Array<{ op: string; operand: 'signal' | 'number'; label: string }> = [
  { op: '+', operand: 'signal', label: 'plus another signal' },
  { op: '-', operand: 'signal', label: 'minus another signal' },
  { op: '/', operand: 'signal', label: 'divided by another signal' },
  { op: '*', operand: 'number', label: 'times a number' },
  { op: '/', operand: 'number', label: 'divided by a number' },
  { op: '+', operand: 'number', label: 'plus a number' },
  { op: '-', operand: 'number', label: 'minus a number' },
  { op: '*', operand: 'signal', label: 'times another signal' },
  { op: 'max', operand: 'signal', label: 'highest of this and another signal' },
  { op: 'min', operand: 'signal', label: 'lowest of this and another signal' },
];

/* The sections of "+ condition", each in the colour its blocks are drawn in
   (sketches/argus-condition-menu.html, option A). */
const SECTION_DEFINES: MenuSection = { name: 'Named conditions (Variables)', color: '--argus-k-def', counted: true };
const SECTION_COMPARE: MenuSection = { name: 'Compare a signal', color: '--argus-k-cmp' };
const SECTION_DATA: MenuSection = { name: 'Data checks', color: '--argus-k-time' };
const SECTION_GROUPS: MenuSection = { name: 'Groups', color: '--argus-k-logic' };

const ADD_CHOICES: Array<{ kind: BlockKind; label: string; hint: string; section: MenuSection; glyph: MenuGlyph }> = [
  { kind: 'compare', label: 'Above or below a value', hint: 'kW above 250', section: SECTION_COMPARE, glyph: { shape: 'pill', color: '--argus-k-cmp' } },
  { kind: 'between', label: 'Between two values', hint: 'temperature between 18 and 30', section: SECTION_COMPARE, glyph: { shape: 'pill', color: '--argus-k-cmp' } },
  { kind: 'average', label: 'Rolling average above or below', hint: '24 hour average above 180', section: SECTION_COMPARE, glyph: { shape: 'pill', color: '--argus-k-cmp' } },
  { kind: 'valid', label: 'Has a reading', hint: 'keeps gaps from counting', section: SECTION_DATA, glyph: { shape: 'dot', color: '--argus-k-time' } },
  { kind: 'stale', label: 'Gone quiet', hint: 'no reading for 6 hours', section: SECTION_DATA, glyph: { shape: 'dot', color: '--argus-k-time' } },
  { kind: 'all', label: 'ALL of…', hint: 'every condition inside must hold', section: SECTION_GROUPS, glyph: { shape: 'rail', color: '--argus-k-all' } },
  { kind: 'any', label: 'ANY of…', hint: 'one condition inside is enough', section: SECTION_GROUPS, glyph: { shape: 'rail', color: '--argus-k-any' } },
  { kind: 'at_least', label: 'AT LEAST N of…', hint: 'two of these four chillers', section: SECTION_GROUPS, glyph: { shape: 'rail', color: '--argus-k-any' } },
  { kind: 'none', label: 'NONE of…', hint: 'not one condition inside may hold', section: SECTION_GROUPS, glyph: { shape: 'rail', color: '--argus-k-none' } },
];

/** How each kind of group is named, offered and joined on screen. */
/** The kinds a group can be changed to, named for its size as its head is (`groupWords`). */
const GROUPS: Array<{ kind: GroupKind; hint: string }> = [
  { kind: 'all', hint: 'every one must be true' },
  { kind: 'any', hint: 'one is enough' },
  { kind: 'at_least', hint: 'a count of them must be true' },
  { kind: 'none', hint: 'not one may be true' },
  { kind: 'not_all', hint: 'some may be true, not all of them' },
];

/** A heading in a menu. Items that share one are drawn under it, in order. */
type MenuSection = { name: string; color: string; counted?: boolean };
/** The small shape before an item: the block it adds, in its colour (a CSS variable). */
type MenuGlyph = { shape: 'hex' | 'pill' | 'dot' | 'rail'; color: string };
type MenuItem = {
  label: string;
  hint?: string;
  current?: boolean;
  run: () => void;
  section?: MenuSection;
  glyph?: MenuGlyph;
  /** The hint is a formula or a condition: set in the mono face. */
  monoHint?: boolean;
};

type Pop =
  | { kind: 'menu'; x: number; y: number; items: MenuItem[] }
  | { kind: 'number'; x: number; y: number; value: number; integer?: boolean; commit: (v: number) => void }
  | { kind: 'text'; x: number; y: number; value: string; commit: (v: string) => void }
  | {
      kind: 'series';
      x: number;
      y: number;
      filter: string;
      commit: (name: string) => void;
      /** Offer the rule's own signals too, and what to do when one is picked. */
      signal?: (key: string) => void;
      /** The named condition being edited: signals made from it are not offered. */
      owner?: string | null;
    }
  | {
      kind: 'formula';
      x: number;
      y: number;
      /** The signal being changed, or null for a new one. */
      key: string | null;
      label: string;
      value: string;
      error: string | null;
      busy: boolean;
    }
  | { kind: 'entities'; x: number; y: number; filter: string; chosen: string[]; title: string; commit: (ids: string[]) => void };

const pathKey = (path: Path): string => path.join('.');
const parsePath = (key: string): Path => key.split('.').map((p) => (/^\d+$/.test(p) ? Number(p) : p));

/** What Backtest hears from the editor on every change. */
export interface QuickChange {
  document: ArgusTreeDocument;
  /** The server read it as a whole rule. */
  ok: boolean;
  sentence: string | null;
  /** Why it is not whole yet; null while the sentence is being read. */
  error: string | null;
  /** How many entities its scope reaches today, once known, and their word. */
  entities: number | null;
  noun: string;
}

@customElement('argus-rule-editor')
export class ArgusRuleEditor extends LightElement {
  /** The rule being changed, or null for a new one. */
  @property({ attribute: false }) rule: ArgusRule | null = null;
  @property({ attribute: false }) sourceKey = 'historical';
  /** A draft the Assistant proposed: opened in place of the rule's own
   *  document (or a blank one), unsaved. */
  @property({ attribute: false }) draft: AssistantRuleDraft | null = null;
  /** Backtest: the blocks and their sentence only - no name, no save, no
   *  switch, no schedule, no severity. Every change is told to the page as
   *  `argus-quick-change`; the page asks the watcher. */
  @property({ type: Boolean }) quick = false;
  /** Backtest with a rule picked: its blocks are shown, not changed. */
  @property({ type: Boolean }) readonly = false;

  @state() private doc: ArgusTreeDocument | null = null;
  @state() private name = '';
  @state() private description = '';
  /** Which heading field is open for typing. A new rule opens on its name. */
  @state() private editingHead: 'name' | 'about' | null = null;
  @state() private preview: ArgusPreview | null = null;
  @state() private scopeInfo: ArgusScopeSummary | null = null;
  @state() private entities: ArgusEntity[] = [];
  @state() private pop: Pop | null = null;
  @state() private saving = false;
  /** The last error toast, closed when the next try starts: a sticky toast
   *  per press would pile up. */
  private errorToast: HTMLElement | null = null;
  /** What the last save did, said beside the button until the next change. */
  @state() private savedNote: string | null = null;
  /** The document before the last Simplify, and which group it changed: its
   *  Undo shows on that group until the next change. */
  @state() private simplified: { before: ArgusTreeDocument; at: string } | null = null;
  /** The rule's stored document as this editor writes it, to tell a change to
   *  the rule itself (a new version) from a change of name or description
   *  (the same version, and its backtest still counts - decision 32). Null
   *  when nothing is stored yet. */
  private baseline: string | null = null;
  /** Which zones are open. Rule starts open; Backtest, Charts and Schedule
   *  start folded. A folded zone stays drawn, so a running backtest keeps
   *  going; its heading carries a one-line summary. */
  @state() private zoneOpen: Record<'rule' | 'backtest' | 'charts' | 'schedule', boolean> = {
    rule: true,
    backtest: false,
    charts: false,
    schedule: false,
  };
  /** The Add or Edit chart dialog: which chart, or null for a new one. */
  @state() private chartDialog: { index: number | null } | null = null;
  /** The chart row whose ⋯ menu is open. */
  @state() private chartMenu: number | null = null;
  @state() private chartsError: string | null = null;
  @state() private backtestSummary: { text: string; tone: string } = { text: 'Not run yet', tone: 'none' };
  /** The Assistant's last change here, with what was there before it: its
   *  Undo shows until the next change. */
  @state() private assistantChange: {
    summary: string;
    before: { doc: ArgusTreeDocument; name: string; description: string };
  } | null = null;
  private unsubscribeAssistant: (() => void) | null = null;
  /** The list, so the Schedule zone shows the rule as it is now: `rule` is the
   *  copy the editor opened with, and a schedule applied here changes it. */
  private store = new StoreController(this, [rules]);

  private previewTimer: number | undefined;
  private previewSeq = 0;
  private scopeKey = '';

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('pointerdown', this.onOutside, true);
    document.addEventListener('keydown', this.onKey);
    this.unsubscribeAssistant = assistantEditorEdit.subscribe(() => this.takeAssistantEdit());
    void this.start();
  }

  override disconnectedCallback(): void {
    document.removeEventListener('pointerdown', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey);
    window.clearTimeout(this.previewTimer);
    // Its errors are about this editor; closed, they say nothing.
    this.clearError();
    this.unsubscribeAssistant?.();
    this.unsubscribeAssistant = null;
    if (openEditor.value && this.publishedFrom === openEditor.value) openEditor.set(null);
    super.disconnectedCallback();
  }

  /* ── what the Assistant is told, and what it changes ───── */

  /** The state this editor last published, to clear only its own on close. */
  private publishedFrom: object | null = null;

  /** Tell the Assistant what is open, after every change that is drawn. */
  override updated(changed: Map<PropertyKey, unknown>): void {
    super.updated(changed);
    const doc = this.doc;
    if (!doc) return;
    const live = this.live();
    const state = {
      mode: this.quick ? ('question' as const) : ('rule' as const),
      ruleId: this.quick ? null : (this.rule?.id ?? null),
      version: this.quick ? null : (live?.version ?? null),
      status: this.quick ? null : (live?.status ?? null),
      name: this.name,
      description: this.description,
      document: normalize(doc),
      dirty: !this.quick && (!live || this.docChanged() || this.labelsChanged()),
      readonly: this.readonly,
    };
    this.publishedFrom = state;
    openEditor.set(state);
  }

  /** A change the Assistant wrote for this editor: applied unsaved, with Undo. */
  private takeAssistantEdit(): void {
    const edit: AssistantEditorEdit | null = assistantEditorEdit.value;
    if (!edit || !this.doc || this.readonly) return;
    const mode = this.quick ? 'question' : 'rule';
    const ruleId = this.quick ? null : (this.rule?.id ?? null);
    if (edit.mode !== mode || edit.ruleId !== ruleId) return;
    assistantEditorEdit.set(null);
    const before = { doc: this.doc, name: this.name, description: this.description };
    if (edit.name !== null && !this.quick) this.name = edit.name;
    if (edit.description !== null && !this.quick) this.description = edit.description;
    this.apply(editable(edit.document));
    this.assistantChange = { summary: edit.summary, before };
  }

  private undoAssistantEdit(): void {
    const change = this.assistantChange;
    if (!change) return;
    this.name = change.before.name;
    this.description = change.before.description;
    this.apply(change.before.doc);
  }

  private renderAssistantChange(): TemplateResult | typeof nothing {
    const change = this.assistantChange;
    if (!change) return nothing;
    return html`<p class="argus-e-saved argus-e-assisted" role="status">
      <b>SONAR changed this:</b> ${change.summary || 'see the blocks.'} Not saved.
      <button type="button" class="argus-lmore" @click=${() => this.undoAssistantEdit()}>Undo</button>
    </p>`;
  }

  private onOutside = (event: Event): void => {
    const pop = this.pop;
    if (!pop) return;
    const target = event.target as Element | null;
    if (target?.closest('.argus-e-pop')) return;
    // A typed value is kept when the person clicks elsewhere, as a spreadsheet
    // cell keeps it - losing it to a stray click is the surprise.
    if (pop.kind === 'number' || pop.kind === 'text') {
      const input = this.querySelector<HTMLInputElement>('.argus-e-pop input');
      if (input) this.commitField(pop, input);
    }
    if (!target?.closest('[data-act]')) this.pop = null;
  };

  private commitField(pop: Extract<Pop, { kind: 'number' | 'text' }>, el: HTMLInputElement): void {
    this.pop = null;
    if (pop.kind === 'number') {
      const v = Number(el.value);
      if (el.value.trim() !== '' && Number.isFinite(v)) pop.commit(pop.integer ? Math.round(v) : v);
    } else {
      pop.commit(el.value);
    }
  }

  private onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.pop) this.pop = null;
  };

  private async start(): Promise<void> {
    const source = this.draft?.document.source ?? this.rule?.tree?.document.source ?? this.sourceKey;
    try {
      this.entities = (await listArgusEntities(source)).entities ?? [];
    } catch {
      this.entities = [];
    }
    if (this.draft) {
      this.doc = editable(this.draft.document);
      this.name = this.draft.name || this.rule?.name || '';
      this.description = this.draft.description || this.rule?.description || '';
    } else if (this.rule?.tree) {
      this.doc = editable(this.rule.tree.document);
      this.name = this.rule.name;
      this.description = this.rule.description ?? '';
    } else {
      const firstTag = this.entities.flatMap((e) => e.tags)[0];
      const scope: ArgusTreeScope = firstTag ? { tags: [firstTag] } : { all: true };
      this.doc = editable(blankDocument(source, scope));
      this.name = '';
      this.description = '';
      if (!this.quick) this.editingHead = 'name';
    }
    this.baseline = this.rule?.tree ? JSON.stringify(normalize(editable(this.rule.tree.document))) : null;
    this.changed();
  }

  /** The rule as the list has it now: a save or a switch here changes it. */
  private live(): ArgusRule | null {
    const id = this.rule?.id;
    return id ? (rules.value.find((r) => r.id === id) ?? this.rule) : null;
  }

  /** Whether saving would write a new version: the rule itself has changed. */
  private docChanged(): boolean {
    return !this.doc || this.baseline === null || JSON.stringify(normalize(this.doc)) !== this.baseline;
  }

  private labelsChanged(): boolean {
    const live = this.live();
    return !live || this.name.trim() !== live.name || this.description.trim() !== (live.description ?? '');
  }

  /* ── keeping the sentence and the scope in step ─────────── */

  private apply(next: ArgusTreeDocument): void {
    // One Conditions clause: a gate joins the condition, except on a silence
    // rule, which has no condition and keeps its gate as a group to add to.
    this.doc =
      next.shape.kind === 'silence'
        ? next.gate
          ? next
          : { ...next, gate: { op: 'and', args: [] } as ArgusTreeNode }
        : foldGate(next);
    this.savedNote = null;
    this.simplified = null;
    this.assistantChange = null;
    this.clearError();
    this.changed();
  }

  /** Backtest: tell the page what the question is now, and whether it is whole. */
  private tellQuick(pending = false): void {
    if (!this.quick || !this.doc) return;
    const preview = pending ? null : this.preview;
    this.dispatchEvent(
      new CustomEvent<QuickChange>('argus-quick-change', {
        detail: {
          document: normalize(this.doc),
          ok: preview?.ok === true,
          sentence: preview?.ok ? preview.sentence : null,
          error: preview && !preview.ok ? preview.error : null,
          entities: this.scopeInfo?.entities.length ?? null,
          noun: this.scopeInfo?.entity_noun ?? 'entity',
        },
        bubbles: true,
      }),
    );
  }

  private changed(): void {
    const doc = this.doc;
    if (!doc) return;
    // Until the server's sentence arrives the question is not known whole.
    this.tellQuick(true);
    const key = JSON.stringify(doc.scope);
    if (key !== this.scopeKey) {
      this.scopeKey = key;
      void resolveArgusScope(doc.source, doc.scope)
        .then((info) => {
          if (JSON.stringify(this.doc?.scope) === key) {
            this.scopeInfo = info;
            this.tellQuick();
          }
        })
        .catch(() => {
          this.scopeInfo = null;
        });
    }
    window.clearTimeout(this.previewTimer);
    this.previewTimer = window.setTimeout(() => void this.refreshPreview(), 300);
  }

  private async refreshPreview(): Promise<void> {
    if (!this.doc) return;
    const seq = ++this.previewSeq;
    if (unchosenInputs(this.doc)) {
      this.preview = { ok: false, error: 'Choose a signal for every block that asks for one.' };
      this.tellQuick();
      return;
    }
    try {
      const answer = await previewArgusRule(normalize(this.doc));
      if (seq === this.previewSeq) this.preview = answer;
    } catch (error) {
      if (seq === this.previewSeq) {
        this.preview = { ok: false, error: userMessage(error, 'Argus could not be reached.') };
      }
    }
    if (seq === this.previewSeq) this.tellQuick();
  }

  /* ── opening things beside what was clicked ─────────────── */

  private place(target: Element): { x: number; y: number } {
    const host = this.getBoundingClientRect();
    const box = target.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(box.left - host.left, host.width - 280)),
      y: box.bottom - host.top + 4,
    };
  }

  private menu(target: Element, items: MenuItem[]): void {
    this.pop = { kind: 'menu', ...this.place(target), items };
  }

  private onClick(event: Event): void {
    const button = (event.target as Element).closest<HTMLElement>('[data-act]');
    if (!button || !this.doc) return;
    event.preventDefault();
    const act = button.dataset.act as string;
    const path = button.dataset.path ? parsePath(button.dataset.path) : [];
    this.act(act, path, button);
  }

  private act(act: string, path: Path, target: Element): void {
    const doc = this.doc as ArgusTreeDocument;
    const at = { ...this.place(target) };
    switch (act) {
      case 'series':
        this.pop = {
          kind: 'series',
          ...at,
          filter: '',
          commit: (name) => {
            const [next, alias] = inputFor(doc, name);
            this.apply(setAt(next, path, { input: alias }));
          },
          signal: (key) => this.apply(setAt(doc, path, { ref: key })),
          owner: path[0] === 'define' ? (path[1] as string) : null,
        };
        return;
      case 'sig-add':
        this.pop = {
          kind: 'formula',
          ...at,
          key: null,
          label: `signal ${signalKeys(doc).length + 1}`,
          value: '',
          error: null,
          busy: false,
        };
        return;
      case 'sig-edit': {
        const key = path[1] as string;
        const entry = doc.define[key];
        this.pop = {
          kind: 'formula',
          ...at,
          key,
          label: entry?.label ?? key,
          value: formulaOf(entry?.expr, doc) ?? '',
          error: null,
          busy: false,
        };
        return;
      }
      case 'param': {
        const name = (getAt(doc, path) as { param: string }).param;
        const entry = doc.params[name];
        this.pop = {
          kind: 'number',
          ...at,
          value: entry?.value ?? 0,
          commit: (v) => this.apply(setAt(doc, ['params', name, 'value'], v)),
        };
        return;
      }
      case 'const':
        this.pop = {
          kind: 'number',
          ...at,
          value: (getAt(doc, path) as { const: number }).const,
          commit: (v) => this.apply(setAt(doc, [...path, 'const'], v)),
        };
        return;
      case 'cmp':
        this.menu(
          target,
          COMPARISONS.map((op) => ({
            label: COMPARE_WORDS[op] as string,
            current: (getAt(doc, [...path, 'op']) as string) === op,
            run: () => this.apply(setAt(doc, [...path, 'op'], op)),
          })),
        );
        return;
      case 'range': {
        const op = getAt(doc, [...path, 'op']) as string;
        this.apply(setAt(doc, [...path, 'op'], op === 'between' ? 'outside' : 'between'));
        return;
      }
      case 'agg':
        this.menu(
          target,
          AGGREGATES.map((op) => ({
            label: AGGREGATE_WORDS[op] as string,
            current: getAt(doc, [...path, 'op']) === op,
            run: () => this.apply(setAt(doc, [...path, 'op'], op)),
          })),
        );
        return;
      case 'window':
      case 'dur': {
        const choices = act === 'window' ? WINDOWS : DURATIONS;
        this.menu(
          target,
          choices.map((d) => ({
            label: durationWords(d),
            current: getAt(doc, path) === d,
            run: () => this.apply(setAt(doc, path, d)),
          })),
        );
        return;
      }
      case 'join': {
        const current = groupKind(getAt(doc, path));
        this.menu(
          target,
          GROUPS.map((g) => ({
            label:
              g.kind === 'at_least'
                ? 'AT LEAST N of these'
                : `${groupWords(g.kind, groupItems(getAt(doc, path) as never, path).items.length).head} of these`,
            hint: g.hint,
            current: current === g.kind,
            run: () => this.apply(setGroupKind(doc, path, g.kind)),
          })),
        );
        return;
      }
      case 'k':
        this.pop = {
          kind: 'number',
          ...at,
          integer: true,
          value: getAt(doc, [...path, 'args', 0]) as number,
          commit: (v) => this.apply(setGroupCount(doc, path, v)),
        };
        return;
      case 'arith':
        this.menu(
          target,
          ARITH_CHOICES.map((choice) => ({
            label: choice.label,
            run: () => {
              const current = getAt(doc, path) as ArgusTreeNode;
              let next = doc;
              let operand: ArgusTreeNode;
              if (choice.operand === 'signal') {
                let alias: string;
                [next, alias] = inputFor(doc, '');
                operand = { input: alias };
              } else {
                let name: string;
                [next, name] = newParam(doc, choice.op === '*' || choice.op === '/' ? 1 : 0);
                operand = { param: name };
              }
              this.apply(setAt(next, path, { op: choice.op, args: [current, operand] } as ArgusTreeNode));
            },
          })),
        );
        return;
      case 'arith-op': {
        const node = getAt(doc, path) as { op: string; args: ArgusTreeNode[] };
        this.menu(target, [
          ...Object.entries(ARITH_WORDS).map(([op, words]) => ({
            label: words,
            current: node.op === op,
            run: () => this.apply(setAt(doc, [...path, 'op'], op)),
          })),
          {
            label: 'keep only the first part',
            hint: 'undo this sum',
            run: () => this.apply(setAt(doc, path, node.args[0])),
          },
          {
            label: 'keep only the second part',
            run: () => this.apply(setAt(doc, path, node.args[1])),
          },
        ]);
        return;
      }
      case 'fn': {
        const node = getAt(doc, path) as { op: string; args: ArgusTreeNode[] };
        this.menu(target, [
          ...(node.op === 'abs'
            ? []
            : Object.entries(FUNCTION_WORDS)
                .filter(([op]) => op !== 'abs')
                .map(([op, words]) => ({
                  label: words,
                  hint: op.toUpperCase(),
                  current: node.op === op,
                  run: () => this.apply(setAt(doc, [...path, 'op'], op)),
                }))),
          {
            label: 'keep only the first value',
            run: () => this.apply(setAt(doc, path, node.args[0] as ArgusTreeNode)),
          },
        ]);
        return;
      }
      case 'fn-add': {
        const args = getAt(doc, [...path, 'args']) as ArgusTreeNode[];
        const [next, alias] = inputFor(doc, '');
        this.apply(setAt(next, [...path, 'args'], [...args, { input: alias }]));
        return;
      }
      case 'fn-drop': {
        const owner = path.slice(0, -2);
        const drop = path[path.length - 1] as number;
        const args = getAt(doc, [...owner, 'args']) as ArgusTreeNode[];
        this.apply(setAt(doc, [...owner, 'args'], args.filter((_, i) => i !== drop)));
        return;
      }
      case 'ref':
        this.menu(
          target,
          Object.entries(doc.define).filter(([, entry]) => !isNumberNode(entry.expr, doc)).map(([key, entry]) => ({
            label: entry.label,
            current: getAt(doc, [...path, 'ref']) === key,
            run: () => this.apply(setAt(doc, path, { ref: key })),
          })),
        );
        return;
      case 'remove':
        this.apply(removeItem(doc, path));
        return;
      case 'truth':
        this.apply(toggleNot(doc, path));
        return;
      case 'simplify':
        this.apply(simplifyAt(doc, path));
        this.simplified = { before: doc, at: pathKey(path) };
        return;
      case 'unsimplify':
        if (this.simplified) this.apply(this.simplified.before);
        return;
      case 'add': {
        // Inside a named condition, not that condition itself, nor anything
        // made from it: it would be defined by itself.
        const self = path[0] === 'define' ? (path[1] as string) : null;
        const usable = ([key, entry]: [string, { expr: unknown }]): boolean =>
          key !== self && !(self && usesDefine(entry.expr, doc, self));
        this.menu(target, [
          ...Object.entries(doc.define).filter((e) => usable(e) && !isNumberNode(e[1].expr, doc)).map(([key, entry]) => ({
            label: entry.label,
            hint: conditionText(entry.expr, doc),
            monoHint: true,
            section: SECTION_DEFINES,
            glyph: { shape: 'hex' as const, color: '--argus-k-def' },
            run: () => this.apply(pushAt(doc, path, { ref: key })),
          })),
          ...ADD_CHOICES.map((choice) => ({
            label: choice.label,
            hint: choice.hint,
            section: choice.section,
            glyph: choice.glyph,
            run: () => {
              const [next, node] = template(doc, choice.kind);
              this.apply(pushAt(next, path, node));
            },
          })),
        ]);
        return;
      }
      case 'scope':
        this.scopeMenu(target);
        return;
      case 'except': {
        const scope = doc.scope as { except?: string[] };
        this.pop = {
          kind: 'entities',
          ...at,
          filter: '',
          title: 'Leave out',
          chosen: [...(scope.except ?? [])],
          commit: (ids) => {
            const next = { ...doc.scope } as ArgusTreeScope & { except?: string[] };
            if (ids.length) next.except = ids;
            else delete next.except;
            this.apply(setAt(doc, ['scope'], next));
          },
        };
        return;
      }
      case 'shape':
        this.menu(target, [
          { label: 'for at least… (sustained)', hint: 'most real deviations', current: doc.shape.kind === 'sustained', run: () => this.apply(setAt(doc, ['shape'], { kind: 'sustained', dwell: '1h' })) },
          { label: 'on any single reading', hint: 'hard limits; noisy for anything else', current: doc.shape.kind === 'single', run: () => this.apply(setAt(doc, ['shape'], { kind: 'single' })) },
          { label: 'in N of the last M windows', hint: 'noisy signals', current: doc.shape.kind === 'n_of_m', run: () => this.apply(setAt(doc, ['shape'], { kind: 'n_of_m', n: 3, m: 5, every: '24h' })) },
          {
            label: 'when a signal stops arriving',
            hint: 'a dead sensor - its own kind of rule',
            current: doc.shape.kind === 'silence',
            run: () => {
              const [next, alias] = inputFor(doc, '');
              this.apply(setAt(next, ['shape'], { kind: 'silence', input: alias, after: '6h' }));
            },
          },
        ]);
        return;
      case 'silence-series':
        this.pop = {
          kind: 'series',
          ...at,
          filter: '',
          commit: (name) => {
            const [next, alias] = inputFor(doc, name);
            this.apply(setAt(next, ['shape', 'input'], alias));
          },
        };
        return;
      case 'int':
        this.pop = {
          kind: 'number',
          ...at,
          integer: true,
          value: getAt(doc, path) as number,
          commit: (v) => this.apply(setAt(doc, path, Math.max(1, Math.round(v)))),
        };
        return;
      case 'sev':
        this.menu(
          target,
          (['info', 'watch', 'critical'] as const).map((s) => ({
            label: s,
            current: doc.severity === s,
            run: () => this.apply(setAt(doc, ['severity'], s)),
          })),
        );
        return;
      case 'def-add':
        this.apply(addDefine(doc, `condition ${Object.keys(doc.define).length + 1}`));
        return;
      case 'def-dup':
        this.apply(duplicateDefine(doc, path[1] as string)[0]);
        return;
      case 'def-label': {
        const key = path[1] as string;
        this.pop = {
          kind: 'text',
          ...at,
          value: doc.define[key]?.label ?? '',
          commit: (v) => {
            if (v.trim()) this.apply(setAt(doc, ['define', key, 'label'], v.trim()));
          },
        };
        return;
      }
      case 'def-remove': {
        const key = path[1] as string;
        const next = normalize(doc);
        if (refsInUse(next).has(key)) {
          this.fail(`"${doc.define[key]?.label}" is still used. Remove it from the rule first.`);
          return;
        }
        const copy = JSON.parse(JSON.stringify(doc)) as ArgusTreeDocument;
        delete copy.define[key];
        this.apply(copy);
        return;
      }
    }
  }

  private scopeMenu(target: Element): void {
    const doc = this.doc as ArgusTreeDocument;
    const noun = this.scopeInfo?.entity_noun ?? 'entity';
    const tags = [...new Set(this.entities.flatMap((e) => e.tags))].sort();
    const setScope = (scope: ArgusTreeScope): void => this.apply(setAt(doc, ['scope'], scope));
    this.menu(target, [
      ...tags.map((tag) => ({
        label: `every ${tag.includes(':') ? tag.split(':')[1] : tag} ${noun}`,
        hint: `${this.entities.filter((e) => e.tags.includes(tag)).length} today`,
        current: 'tags' in doc.scope && doc.scope.tags.length === 1 && doc.scope.tags[0] === tag,
        run: () => setScope({ tags: [tag] }),
      })),
      {
        label: `every ${noun}`,
        hint: `${this.entities.length} today`,
        current: 'all' in doc.scope,
        run: () => setScope({ all: true }),
      },
      {
        label: `chosen ${noun}s…`,
        hint: 'pick them one by one',
        current: 'entities' in doc.scope,
        run: () => {
          const chosen = 'entities' in doc.scope ? [...doc.scope.entities] : [];
          this.pop = {
            kind: 'entities',
            ...this.place(target),
            filter: '',
            title: `Watch these ${noun}s`,
            chosen,
            commit: (ids) => {
              if (ids.length) setScope({ entities: ids });
            },
          };
        },
      },
    ]);
  }

  /* ── drawing ─────────────────────────────────────────────── */

  private pill(act: string, path: Path, cls: string, body: TemplateResult | string, title?: string): TemplateResult {
    return html`<button
      type="button"
      class="argus-b-pill ${cls} is-edit"
      data-act=${act}
      data-path=${pathKey(path)}
      title=${title ?? nothing}
    >
      ${body}
    </button>`;
  }

  private word(text: string, act?: string, path?: Path): TemplateResult {
    if (!act) return html`<span class="argus-b-word">${text}</span>`;
    return html`<button type="button" class="argus-b-word is-edit" data-act=${act} data-path=${pathKey(path ?? [])}>
      ${text}
    </button>`;
  }

  /** `notAt`: the condition must be false - red, with a NOT that makes it true again. */
  private hex(kind: string, body: TemplateResult, notAt?: Path): TemplateResult {
    return html`<span class="argus-b-hex is-${kind} ${notAt ? 'is-not' : ''}"
      ><span class="argus-b-in">${notAt ? this.notWord(notAt) : nothing}${body}</span></span
    >`;
  }

  /**
   * A comparison with a computed side, as a card: a head that always shows
   * Must be true / Must be false and the remove, one row (left, comparison,
   * right) and the same condition as a formula line. Square, so three lines
   * of values stay a box and never stretch a hexagon (2026-10-22).
   */
  private card(row: TemplateResult, formula: string, path: Path, notAt: Path | undefined, item: boolean): TemplateResult {
    const at = pathKey(notAt ?? path);
    const not = !!notAt;
    return html`<div class="argus-e-card ${not ? 'is-not' : ''}">
      <div class="argus-e-card-head">
        <span class="argus-e-card-tag">Condition</span>
        <span class="argus-e-truth" role="group" aria-label="Must be true or false">
          <button type="button" aria-pressed=${not ? 'false' : 'true'} data-act=${not ? 'truth' : nothing} data-path=${not ? at : nothing}>Must be true</button
          ><button type="button" aria-pressed=${not ? 'true' : 'false'} data-act=${not ? nothing : 'truth'} data-path=${not ? nothing : at}>Must be false</button>
        </span>
        ${item
          ? html`<button type="button" class="argus-e-remove is-shown" aria-label="Remove condition" title="Remove condition" data-act="remove" data-path=${at}>×</button>`
          : nothing}
      </div>
      <div class="argus-e-card-row">${row}</div>
      <p class="argus-e-card-formula">${not ? 'NOT ' : ''}${formula}</p>
    </div>`;
  }

  private notWord(path: Path): TemplateResult {
    return html`<button type="button" class="argus-b-not is-edit" data-act="truth" data-path=${pathKey(path)} title="Must be false. Click: must be true">not</button>`;
  }

  private arg(node: unknown, path: Path, act = 'dur'): TemplateResult {
    if (typeof node === 'string') return this.pill(act, path, 'is-time', durationWords(node));
    if (typeof node === 'number') return this.pill('int', path, 'is-num', String(node));
    return this.node(node as ArgusTreeNode, path);
  }

  /**
   * A number somebody may want to do arithmetic on: the value, and a small ±
   * that shows on hover and opens "plus another signal", "divided by a
   * number" and the rest. No button row - the glyph is there when you look.
   */
  private num(node: unknown, path: Path): TemplateResult {
    if (typeof node !== 'object' || node === null) return this.arg(node, path);
    return html`<span class="argus-e-num">${this.arg(node, path)}<button
        type="button"
        class="argus-e-arith"
        aria-label="Do arithmetic on this"
        title="Plus, minus, times, divided by"
        data-act="arith"
        data-path=${pathKey(path)}
      >±</button></span>`;
  }

  /**
   * `notAt`: set when the node is a condition that must be false, at that path.
   * `item`: the node is an item of a group, so a card draws its own remove.
   */
  private node(node: ArgusTreeNode, path: Path, notAt?: Path, item = false): TemplateResult {
    const doc = this.doc as ArgusTreeDocument;
    // NONE of one condition: drawn as that condition in red, with NOT, no box.
    const neg = negated(node);
    if (neg) return this.node(neg.inner, [...path, ...neg.sub], path, item);
    if ('input' in node) {
      const series = doc.inputs[node.input]?.series ?? '';
      const reach = this.scopeInfo?.series.find((s) => s.name === series);
      const total = this.scopeInfo?.entities.length ?? 0;
      const partial = reach && total && reach.carried_by < total;
      return this.pill(
        'series',
        path,
        `is-series ${series ? '' : 'is-empty'} ${partial ? 'is-partial' : ''}`,
        series || 'choose a signal',
        partial ? `Carried by ${reach.carried_by} of ${total}` : undefined,
      );
    }
    if ('param' in node) {
      const entry = doc.params[node.param];
      return this.pill('param', path, 'is-num', html`${entry?.value ?? '?'}${entry?.unit ? html` <span class="argus-b-unit">${entry.unit}</span>` : nothing}`);
    }
    if ('const' in node) return this.pill('const', path, 'is-num', String(node.const));
    if ('ref' in node && isNumberNode(node, doc)) {
      // A signal from the Where clause, standing where a series can: picked
      // and changed with the same picker as a series.
      return this.pill('series', path, 'is-series is-signal', doc.define[node.ref]?.label ?? node.ref, 'A custom signal of this rule');
    }
    if ('ref' in node && notAt) {
      return this.hex('def', this.word(doc.define[node.ref]?.label ?? node.ref, 'ref', path), notAt);
    }
    if ('ref' in node) {
      return html`<button type="button" class="argus-b-hex is-def is-edit" data-act="ref" data-path=${pathKey(path)}>
        <span class="argus-b-in">${doc.define[node.ref]?.label ?? node.ref}</span>
      </button>`;
    }
    const args = node.args;
    const p = (i: number): Path => [...path, 'args', i];
    if (isCard(node)) {
      return this.card(
        html`${this.num(args[0], p(0))}${this.pill('cmp', path, 'is-op has-caret', COMPARE_WORDS[node.op] as string, 'Change the comparison')}${this.num(args[1], p(1))}`,
        formulaText(node, doc),
        path,
        notAt,
        item,
      );
    }
    if (node.op in COMPARE_WORDS) {
      return this.hex('cmp', html`${this.num(args[0], p(0))}${this.word(COMPARE_WORDS[node.op] as string, 'cmp', path)}${this.num(args[1], p(1))}`, notAt);
    }
    if (node.op === 'between' || node.op === 'outside') {
      return this.hex(
        'cmp',
        html`${this.num(args[0], p(0))}${this.word(node.op, 'range', path)}${this.arg(args[1], p(1))}${this.word(node.op === 'between' ? 'and' : 'to')}${this.arg(args[2], p(2))}`,
        notAt,
      );
    }
    if (groupKind(node)) {
      return this.group(node as { op: string; args: ArgusTreeNode[] }, path);
    }
    if (node.op === 'valid') return this.hex('quality', html`${this.arg(args[0], p(0))}${this.word('has a reading')}`, notAt);
    if (node.op === 'stale') {
      return this.hex('quality', html`${this.arg(args[0], p(0))}${this.word('no reading for')}${this.arg(args[1], p(1))}`, notAt);
    }
    if (node.op in AGGREGATE_WORDS) {
      return html`<span class="argus-b-round">${this.arg(args[1], p(1), 'window')}${this.word(AGGREGATE_WORDS[node.op] as string, 'agg', path)}${this.word('of')}${this.num(args[0], p(0))}</span>`;
    }
    if (node.op in ARITH_WORDS) {
      return html`<span class="argus-e-arith-box">${this.num(args[0], p(0))}${this.pill(
        'arith-op',
        path,
        'is-arith-op has-caret',
        `${ARITH_SIGNS[node.op]} ${ARITH_WORDS[node.op]}`,
        'Change the arithmetic',
      )}${this.num(args[1], p(1))}</span>`;
    }
    if (node.op in FUNCTION_WORDS) {
      // A labelled box, its values one per line, so it reads as MAX(a, b, c).
      const many = node.op !== 'abs';
      return html`<span class="argus-e-fn">
        <button type="button" class="argus-e-fn-label has-caret" data-act="fn" data-path=${pathKey(path)} title="Change the function">
          ${FUNCTION_WORDS[node.op]}
        </button>
        <span class="argus-e-fn-args">
          ${args.map(
            (a, i) => html`<span class="argus-e-fn-arg">${this.num(a, p(i))}${many && args.length > 2
              ? html`<button type="button" class="argus-e-remove is-shown" aria-label="Remove this value" title="Remove this value" data-act="fn-drop" data-path=${pathKey(p(i))}>×</button>`
              : nothing}</span>`,
          )}
        </span>
        ${many ? html`<button type="button" class="argus-e-add" data-act="fn-add" data-path=${pathKey(path)}>+ value</button>` : nothing}
      </span>`;
    }
    const body = html`<span class="argus-b-round">${this.word(node.op)}${args.map((a, i) => this.arg(a, p(i)))}</span>`;
    return notAt ? html`${this.notWord(notAt)}${body}` : body;
  }

  /**
   * A group, drawn as a bracket: a rail down the left in its kind's colour -
   * ALL purple, ANY orange, NONE and NOT ALL red and dashed, because they
   * negate - its head saying what it asks ("AT LEAST ONE of these 4 is true",
   * click it to change), and the joining word between its conditions, so
   * "and", "or" and "nor" read on the page rather than being remembered from
   * the heading. A nested group sits in a box of its own, one step in.
   *
   * Where a shorter shape means the same, the head offers it - "Simplify ->
   * NOT BOTH" - and Undo after. Nothing is folded without a click: a box that
   * changed shape by itself would move under the mouse (2026-10-08).
   *
   * The clause's own group (`emptyText` set) hides its heading while it holds
   * one plain condition - "ALL of" one thing is noise.
   */
  private group(node: { op: string; args: ArgusTreeNode[] }, path: Path, emptyText?: string): TemplateResult {
    const kind = groupKind(node) ?? 'all';
    const { list, offset, items, k } = groupItems(node, path);
    const root = emptyText !== undefined;
    const count = items.length;
    const showHead = !root || kind === 'none' || kind === 'not_all' || kind === 'at_least' || count > 1;
    const words = groupWords(kind, count);
    const key = pathKey(path);
    const fold = simplification(node);
    const lead = root && path[0] === 'condition' && !this.quick ? html`<span class="argus-b-word is-lead">Alert when</span>` : nothing;
    return html`
      <div class="argus-b-group is-${kind} ${root ? 'is-root' : 'is-nested'}">
        ${showHead
          ? html`<div class="argus-b-group-head">
              ${lead}
              ${this.pill('join', path, 'is-logic', words.head, 'Change how these combine')}
              ${kind === 'at_least' ? this.pill('k', path, 'is-num', String(k ?? 1), 'How many must be true') : nothing}
              ${this.word(words.tail)}
              ${fold
                ? html`<button type="button" class="argus-e-simplify" data-act="simplify" data-path=${key} title="The same meaning, in fewer boxes">
                    Simplify → ${groupWords(fold.to, fold.count).head}
                  </button>`
                : nothing}
              ${this.simplified?.at === key
                ? html`<span class="argus-e-simplified">Simplified.</span
                    ><button type="button" class="argus-e-undo" data-act="unsimplify" data-path=${key}>Undo</button>`
                : nothing}
            </div>`
          : nothing}
        ${items.map((child, i) => {
          const at = [...list, i + offset];
          const isNot = negated(child) !== null;
          const isGroup = !isNot && groupKind(child) !== null;
          const card = isCard(child);
          return html`
            ${i > 0 && words.join && showHead ? html`<span class="argus-b-join" aria-hidden="true">${words.join}</span>` : nothing}
            <div class="argus-e-item ${card ? 'is-card' : ''}">
              ${this.node(child as ArgusTreeNode, at, undefined, true)}
              ${card ? nothing : isGroup
                ? nothing
                : html`<button
                    type="button"
                    class="argus-e-not ${isNot ? 'is-on' : ''}"
                    aria-pressed=${isNot ? 'true' : 'false'}
                    aria-label=${isNot ? 'Must be true' : 'Must be false'}
                    title=${isNot ? 'Must be true' : 'Must be false'}
                    data-act="truth"
                    data-path=${pathKey(at)}
                  >¬</button>`}
              ${card ? nothing : html`<button type="button" class="argus-e-remove" aria-label="Remove" data-act="remove" data-path=${pathKey(at)}>×</button>`}
            </div>`;
        })}
        ${!count && emptyText ? html`<span class="argus-b-word">${emptyText}</span>` : nothing}
        <button type="button" class="argus-e-add" data-act="add" data-path=${pathKey(list)}>${count ? words.add : '+ condition'}</button>
      </div>
    `;
  }

  private renderWatch(doc: ArgusTreeDocument): TemplateResult {
    const info = this.scopeInfo;
    const except = (doc.scope as { except?: string[] }).except ?? [];
    return html`
      ${this.pill('scope', [], 'is-scope', info?.words ?? '…')}
      ${'entities' in doc.scope
        ? nothing
        : this.word(except.length ? `except ${except.length}` : 'except…', 'except', [])}
      ${info
        ? html`<span class="argus-b-note">${info.entities.length} ${info.entity_noun}${info.entities.length === 1 ? '' : 's'} today</span>`
        : nothing}
    `;
  }

  private renderTiming(doc: ArgusTreeDocument): TemplateResult {
    const shape = doc.shape;
    if (shape.kind === 'sustained') {
      return html`${this.word('for at least', 'shape', [])}${this.pill('dur', ['shape', 'dwell'], 'is-time', durationWords(shape.dwell))}`;
    }
    if (shape.kind === 'n_of_m') {
      return html`${this.word('in', 'shape', [])}${this.pill('int', ['shape', 'n'], 'is-num', String(shape.n))}${this.word('of the last')}${this.pill('int', ['shape', 'm'], 'is-num', String(shape.m))}${this.pill('window', ['shape', 'every'], 'is-time', durationWords(shape.every))}${this.word('windows')}`;
    }
    if (shape.kind === 'silence') return this.word('when a signal stops arriving', 'shape', []);
    return this.word('on any single reading', 'shape', []);
  }

  private renderAlert(doc: ArgusTreeDocument): TemplateResult {
    const shape = doc.shape;
    if (shape.kind === 'silence') {
      const series = doc.inputs[shape.input]?.series ?? '';
      // A silence rule has no condition to join, so its gate stays its own
      // group - "only while" - inside the same Conditions clause.
      return html`${this.hex(
        'quality',
        html`${this.word('no reading of')}${this.pill('silence-series', [], `is-series ${series ? '' : 'is-empty'}`, series || 'choose a signal')}${this.word('for')}${this.pill('dur', ['shape', 'after'], 'is-time', durationWords(shape.after))}`,
      )}${this.word('only while')}${this.group(doc.gate as { op: string; args: ArgusTreeNode[] }, ['gate'], 'always')}`;
    }
    return this.group(doc.condition as { op: string; args: ArgusTreeNode[] }, ['condition'], '');
  }

  /**
   * The rule's own definitions, in two sections: **Custom signals** - a name
   * over a formula, in the dialect of Explore's derived signals, a number -
   * and **Variables** (`renderWhere`), the named conditions, true or false. A signal is shown as
   * the formula it reads as, written back from the tree, so one the
   * Assistant wrote reads exactly like one typed here. Both are `define`
   * entries underneath; the server tells them apart by type.
   */
  private renderSignals(doc: ArgusTreeDocument): TemplateResult {
    return html`
      ${signalKeys(doc).map((key) => this.defineRow(doc, key, true))}
      <div class="argus-e-adds">
        <button type="button" class="argus-e-add" data-act="sig-add" data-path="" title="A name over a formula, as in Explore's derived signals">+ signal</button>
      </div>
    `;
  }

  private renderWhere(doc: ArgusTreeDocument): TemplateResult {
    const signals = new Set(signalKeys(doc));
    return html`
      <div class="argus-b-defines">
        ${Object.keys(doc.define).filter((key) => !signals.has(key)).map((key) => this.defineRow(doc, key, false))}
      </div>
      <div class="argus-e-adds">
        <button type="button" class="argus-e-add" data-act="def-add" data-path="">+ named condition</button>
      </div>
    `;
  }

  private defineRow(doc: ArgusTreeDocument, key: string, signal: boolean): TemplateResult {
    const entry = doc.define[key] as { label: string; expr: ArgusTreeNode };
    return signal
        ? html`<div class="argus-b-define is-signal">
            <button type="button" class="argus-b-pill is-series is-signal is-edit" data-act="def-label" data-path=${pathKey(['define', key])} title="Rename">
              ${entry.label}
            </button>
            ${this.word('is')}
            <button type="button" class="argus-e-formula" data-act="sig-edit" data-path=${pathKey(['define', key])} title="Change the formula">
              ${formulaOf(entry.expr, doc) ?? 'a computed value'}
            </button>
            <button type="button" class="argus-e-remove" aria-label="Remove" data-act="def-remove" data-path=${pathKey(['define', key])}>×</button>
          </div>`
        : html`<div class="argus-b-define">
            <button type="button" class="argus-b-hex is-def is-edit" data-act="def-label" data-path=${pathKey(['define', key])} title="Rename">
              <span class="argus-b-in">${entry.label}</span>
            </button>
            ${this.word('means')}
            ${groupKind(entry.expr)
              ? this.group(entry.expr as { op: string; args: ArgusTreeNode[] }, ['define', key, 'expr'], '')
              : this.node(entry.expr, ['define', key, 'expr'])}
            <span class="argus-b-define-acts">
              <button type="button" class="argus-e-dup" title="Duplicate" aria-label="Duplicate" data-act="def-dup" data-path=${pathKey(['define', key])}>❐</button>
              <button type="button" class="argus-e-remove" aria-label="Remove" data-act="def-remove" data-path=${pathKey(['define', key])}>×</button>
            </span>
          </div>`;
  }

  /** Read the formula on the server, then store it as the signal. */
  private async useFormula(): Promise<void> {
    const pop = this.pop;
    const doc = this.doc;
    if (!pop || pop.kind !== 'formula' || !doc) return;
    const label = pop.label.trim();
    if (!label) {
      this.pop = { ...pop, error: 'Give the signal a name.' };
      return;
    }
    this.pop = { ...pop, busy: true, error: null };
    try {
      // The other signals and the named conditions, by label, so this formula may build on them.
      const names = formulaNames(doc, pop.key);
      const answer = await argusFormula(pop.value, doc.inputs, names.signals, names.conditions);
      const [next] = putSignal(doc, pop.key, label, answer.expr, answer.inputs);
      this.pop = null;
      this.apply(next);
    } catch (error) {
      const message = userMessage(error, 'The formula could not be read.');
      if (this.pop?.kind === 'formula') this.pop = { ...this.pop, busy: false, error: message };
    }
  }

  /** The rule's signals, at the top of the series picker. */
  private signalChoices(
    filter: string,
    pick: (key: string) => void,
    owner: string | null = null,
    series = 0,
  ): TemplateResult | typeof nothing {
    const doc = this.doc as ArgusTreeDocument;
    const needle = filter.toLowerCase();
    // A signal made from the condition being edited would define it by itself.
    const keys = signalKeys(doc).filter(
      (key) =>
        (doc.define[key]?.label ?? '').toLowerCase().includes(needle) &&
        !(owner && usesDefine(doc.define[key]?.expr, doc, owner)),
    );
    if (!keys.length) return nothing;
    return html`<small class="argus-e-section" style="--k: var(--argus-k-series)">Custom signals<span class="argus-e-count">${keys.length}</span></small>
      ${keys.map(
        (key) => html`<button type="button" class="argus-e-item-btn has-glyph" @click=${() => pick(key)}>
          <span class="argus-e-glyph is-pill is-filled" style="--k: var(--argus-k-series)"></span>
          <span>${doc.define[key]?.label}</span><small class="argus-e-mono">${formulaOf(doc.define[key]?.expr, doc) ?? ''}</small>
        </button>`,
      )}
      <div class="argus-e-sep" role="separator"></div>
      <small class="argus-e-section" style="--k: var(--argus-k-series)">Series${series ? html`<span class="argus-e-count">${series.toLocaleString('en-GB')}</span>` : nothing}</small>`;
  }

  /** What the formula editor autocompletes: the rule's other signals, its named conditions, then series. */
  private formulaColumns(except: string | null = null): ColumnInfo[] {
    const names = formulaNames(this.doc as ArgusTreeDocument, except);
    return [
      ...Object.keys(names.signals),
      ...Object.keys(names.conditions),
      ...(this.scopeInfo?.series ?? []).map((s) => s.name),
    ].map((name) => ({
      name,
      type: 'DOUBLE',
      null_pct: 0,
      null_count: 0,
      total_count: 0,
      months: [],
    }));
  }

  private renderPop(): TemplateResult | typeof nothing {
    const pop = this.pop;
    if (!pop) return nothing;
    const style = `left:${pop.x}px;top:${pop.y}px`;
    const close = (): void => {
      this.pop = null;
    };
    if (pop.kind === 'menu') {
      return html`<div class="argus-e-pop" style=${style} role="menu">
        ${pop.items.map((item, i) => {
          const section = item.section;
          const opens = section && section.name !== pop.items[i - 1]?.section?.name;
          const count = section?.counted ? pop.items.filter((other) => other.section?.name === section.name).length : 0;
          return html`${opens
              ? html`${i > 0 ? html`<div class="argus-e-sep" role="separator"></div>` : nothing}<small class="argus-e-section" style="--k: var(${section.color})"
                  >${section.name}${count ? html`<span class="argus-e-count">${count}</span>` : nothing}</small
                >`
              : nothing}<button
              type="button"
              role="menuitem"
              class="argus-e-item-btn ${item.current ? 'is-current' : ''} ${item.glyph ? 'has-glyph' : ''}"
              @click=${() => { close(); item.run(); }}
            >
              ${item.glyph ? html`<span class="argus-e-glyph is-${item.glyph.shape}" style="--k: var(${item.glyph.color}, var(--argus-k-logic))"></span>` : nothing}
              <span>${item.label}</span>${item.hint ? html`<small class=${item.monoHint ? 'argus-e-mono' : ''}>${item.hint}</small>` : nothing}
            </button>`;
        })}
      </div>`;
    }
    if (pop.kind === 'number' || pop.kind === 'text') {
      const commit = (el: HTMLInputElement): void => this.commitField(pop, el);
      return html`<div class="argus-e-pop" style=${style}>
        <input
          class="argus-e-input"
          type=${pop.kind === 'number' ? 'number' : 'text'}
          .value=${String(pop.value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter') commit(e.target as HTMLInputElement);
          }}
          ${autofocus()}
        />
        <small>Enter to apply</small>
      </div>`;
    }
    if (pop.kind === 'formula') {
      return html`<div class="argus-e-pop is-formula" style=${style}>
        <strong class="argus-e-title">${pop.key ? 'Change the signal' : 'A new signal'}</strong>
        <input
          class="argus-e-input"
          type="text"
          placeholder="Name it - total generator power"
          maxlength="80"
          .value=${pop.label}
          @input=${(e: Event) => {
            if (this.pop?.kind === 'formula') this.pop = { ...this.pop, label: (e.target as HTMLInputElement).value };
          }}
        />
        <number-formula-editor
          dialect="derived"
          label="Formula"
          .columns=${this.formulaColumns(pop.key)}
          .value=${pop.value}
          .change=${(v: string) => {
            if (this.pop?.kind === 'formula') this.pop = { ...this.pop, value: v, error: null };
          }}
        ></number-formula-editor>
        <small class="argus-e-hint">As in Explore's derived signals: "DG1_ACTIVE_POWER" + "DG2_ACTIVE_POWER", AVG("STW", 1h). A window needs a length. A named condition counts 1 while it holds, else 0: "ME_FUEL" * "sailing".</small>
        ${pop.error ? html`<p class="argus-form-error" role="alert">${pop.error}</p>` : nothing}
        <div class="argus-e-pop-actions">
          <button type="button" class="dash-btn" @click=${close}>Cancel</button>
          <button type="button" class="btn btn-primary" ?disabled=${pop.busy} @click=${() => void this.useFormula()}>
            ${pop.busy ? 'Reading…' : pop.key ? 'Change' : 'Add signal'}
          </button>
        </div>
      </div>`;
    }
    if (pop.kind === 'series') {
      const all = this.scopeInfo?.series ?? [];
      const total = this.scopeInfo?.entities.length ?? 0;
      const needle = pop.filter.toLowerCase();
      const shown = all.filter((s) => s.name.toLowerCase().includes(needle)).slice(0, 120);
      return html`<div class="argus-e-pop is-wide" style=${style}>
        <input
          class="argus-e-input"
          type="search"
          placeholder="Find a signal…"
          .value=${pop.filter}
          @input=${(e: Event) => {
            this.pop = { ...pop, filter: (e.target as HTMLInputElement).value };
          }}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter' && shown[0]) {
              close();
              pop.commit(shown[0].name);
            }
          }}
          ${autofocus()}
        />
        <div class="argus-e-list">
          ${pop.signal ? this.signalChoices(pop.filter, (key) => { close(); pop.signal?.(key); }, pop.owner, all.length) : nothing}
          ${shown.map(
            (s) => html`<button type="button" class="argus-e-item-btn" @click=${() => { close(); pop.commit(s.name); }}>
              <span class="argus-e-mono">${s.name}</span>
              ${total && s.carried_by < total ? html`<small>on ${s.carried_by} of ${total}</small>` : nothing}
            </button>`,
          )}
          ${!shown.length ? html`<small class="argus-e-none">${all.length ? 'Nothing matches.' : 'Reading the signals…'}</small>` : nothing}
        </div>
      </div>`;
    }
    const needle = pop.filter.toLowerCase();
    const shown = this.entities.filter((e) => e.name.toLowerCase().includes(needle) || e.id.toLowerCase().includes(needle));
    return html`<div class="argus-e-pop is-wide" style=${style}>
      <strong class="argus-e-title">${pop.title}</strong>
      <input
        class="argus-e-input"
        type="search"
        placeholder="Find…"
        .value=${pop.filter}
        @input=${(e: Event) => {
          this.pop = { ...pop, filter: (e.target as HTMLInputElement).value };
        }}
        ${autofocus()}
      />
      <div class="argus-e-list">
        ${shown.map(
          (e) => html`<label class="argus-e-check">
            <input
              type="checkbox"
              .checked=${pop.chosen.includes(e.id)}
              @change=${(ev: Event) => {
                const on = (ev.target as HTMLInputElement).checked;
                const chosen = on ? [...pop.chosen, e.id] : pop.chosen.filter((id) => id !== e.id);
                this.pop = { ...pop, chosen };
              }}
            />
            <span>${e.name}</span>
          </label>`,
        )}
      </div>
      <div class="argus-e-pop-foot">
        <span>${pop.chosen.length} chosen</span>
        <button type="button" class="dash-btn is-primary" @click=${() => { close(); pop.commit(pop.chosen); }}>Done</button>
      </div>
    </div>`;
  }

  /**
   * The Check panel: what the rule says, and whom it would watch. The
   * Assistant may join it as a tab with E3 (`docs/plans/argus.md`). Schedule
   * and Backtest are zones of their own under the rule, full width
   * (sketch `docs/design/sketches/argus-rule-editor.html`).
   */
  private renderCheck(): TemplateResult {
    const preview = this.preview;
    const coverage = preview?.ok ? preview.coverage : null;
    const names = new Map((this.scopeInfo?.entities ?? []).map((e) => [e.id, e.name]));
    const noun = this.scopeInfo?.entity_noun ?? 'entity';
    const watched = coverage?.watched ?? [];
    const missing = coverage?.not_watched ?? [];
    const total = watched.length + missing.length;
    return html`
      <aside class="argus-e-side" aria-label="Check">
        <h3 class="argus-e-side-h">Check</h3>
        <section>
          <div class="argus-b-label">Reads as</div>
          ${!preview
            ? html`<p class="argus-e-sentence is-muted">Reading…</p>`
            : preview.ok
              ? html`<p class="argus-e-sentence">${preview.sentence}</p>`
              : html`<p class="argus-e-sentence is-muted">Not ${this.quick ? 'a whole question' : 'a rule'} yet: ${preview.error}</p>`}
        </section>
        ${coverage
          ? html`<section>
              <div class="argus-b-label">Watches ${watched.length} of ${total} ${noun}${total === 1 ? '' : 's'}</div>
              <ul class="argus-e-cover">
                ${missing.map(
                  (n) => html`<li class="is-missing">
                    <span>${n.name}</span><small>missing ${n.missing.join(', ')}</small>
                  </li>`,
                )}
                ${watched.slice(0, 8).map((id) => html`<li><span>${names.get(id) ?? id}</span><small>✓</small></li>`)}
                ${watched.length > 8 ? html`<li class="is-more">and ${watched.length - 8} more</li>` : nothing}
              </ul>
            </section>`
          : nothing}
      </aside>
    `;
  }

  /**
   * One zone of the editor - Rule, Backtest or Schedule - in one style: a
   * heading that folds it, with a one-line summary beside the name.
   */
  private renderZone(
    key: 'rule' | 'backtest' | 'charts' | 'schedule',
    name: string,
    summary: { text: string; tone: string },
    body: TemplateResult,
  ): TemplateResult {
    const open = this.zoneOpen[key];
    return html`<section class="argus-e-zone is-fold ${open ? 'is-open' : ''}" aria-label=${name}>
      <h3 class="argus-e-zone-h">
        <button
          type="button"
          class="argus-e-zone-toggle"
          aria-expanded=${open ? 'true' : 'false'}
          @click=${() => (this.zoneOpen = { ...this.zoneOpen, [key]: !open })}
        >
          <span class="argus-e-zone-caret" aria-hidden="true"></span>
          <span class="argus-e-zone-name">${name}</span>
          <span class="argus-e-zone-sum is-${summary.tone}">${summary.text}</span>
        </button>
      </h3>
      <div class="argus-e-zone-b" ?hidden=${!open}>${body}</div>
    </section>`;
  }

  /** Rule: the blocks and the Check panel; folded, its heading reads the rule. */
  private renderRuleZone(doc: ArgusTreeDocument): TemplateResult {
    const preview = this.preview;
    const summary = !preview
      ? { text: 'Reading…', tone: 'none' }
      : preview.ok
        ? { text: preview.sentence, tone: 'done' }
        : { text: 'Not a rule yet', tone: 'stale' };
    return this.renderZone(
      'rule',
      'Rule',
      summary,
      html`<div class="argus-e-body">
        <div class="argus-b-canvas">
          <div class="argus-b-stack">
            ${clause('watch', 'def', this.renderWatch(doc))}
            ${clause('alert', 'logic', this.renderAlert(doc), false, doc.shape.kind === 'silence' ? nothing : logicKey(doc.condition))}
            ${clause('timing', 'time', this.renderTiming(doc))}
            ${clause('severity', 'logic', html`<button type="button" class="argus-chip is-${doc.severity} is-edit" data-act="sev" data-path="">${doc.severity}</button>`)}
          </div>
          <div class="argus-b-stack">${clause('signals', 'signal', this.renderSignals(doc))}</div>
          <div class="argus-b-stack">${clause('where', 'def', this.renderWhere(doc))}</div>
        </div>
        ${this.renderCheck()}
      </div>`,
    );
  }

  /**
   * Schedule is not part of the version (migration 0032): it applies on its
   * own, straight away, and is not waiting for Save - so it can be changed
   * here even while the rule runs, as in the drawer.
   */
  private renderSchedule(): TemplateResult {
    const live = this.rule ? (rules.value.find((r) => r.id === this.rule!.id) ?? this.rule) : null;
    // Before the first save, the schedule the server will give it: its source's.
    const shown =
      live?.schedule ?? sources.value.find((src) => src.key === this.doc?.source)?.schedule ?? DEFAULT_SCHEDULE;
    const words = scheduleWords(shown);
    return this.renderZone(
      'schedule',
      'Schedule',
      { text: words, tone: 'done' },
      live
        ? html`<p class="argus-lfaint argus-e-zone-note">
              Applies on its own, straight away - not with Save. Not part of the version, so it needs no new backtest.
            </p>
            <argus-schedule-panel .rule=${live} ?editable=${canDo('argus:author')} wide></argus-schedule-panel>`
        : html`<p class="argus-lfaint">
            A new rule runs ${words[0]!.toLowerCase()}${words.slice(1)}. Save it to choose another schedule.
          </p>`,
    );
  }

  /**
   * Charts: what this rule's alerts and backtests show, as tabs, in this order,
   * before Argus's guess (T3, migration 0057). Not part of the version, so a
   * change saves at once - not with Save - also while the rule runs.
   */
  private renderCharts(): TemplateResult {
    const live = this.live();
    const charts = live?.charts ?? [];
    const may = !!live && canDo('argus:author') && !this.readonly;
    const summary = !live
      ? { text: 'Save the rule first', tone: 'stale' }
      : charts.length
        ? { text: `${charts.length} chart${charts.length === 1 ? '' : 's'}, then Argus's guess`, tone: 'done' }
        : { text: "Argus picks the chart from the rule", tone: 'none' };
    const store = async (next: ArgusRuleChart[]): Promise<void> => {
      this.chartMenu = null;
      this.chartsError = await setCharts(live!.id, next);
    };
    const moved = (i: number, by: number): ArgusRuleChart[] => {
      const next = [...charts];
      const [one] = next.splice(i, 1);
      next.splice(i + by, 0, one!);
      return next;
    };
    const row = (c: ArgusRuleChart, i: number): TemplateResult => {
      const type = new URLSearchParams(c.query).get('pt') || 'line';
      return html`<li class="argus-ch-row ${this.chartMenu === i ? 'is-menu' : ''}">
        ${chartIcon(type)}
        <span class="argus-ch-text">
          <span class="argus-ch-title">${c.title}</span>
          <span class="argus-ch-what">${chartWhat(c.query)}</span>
        </span>
        ${i === 0 ? html`<span class="argus-ch-badge is-first">Opens first</span>` : nothing}
        ${may
          ? html`<span class="argus-ch-menu">
              <button
                type="button"
                class="argus-ch-more"
                aria-label=${`More for ${c.title}`}
                aria-expanded=${this.chartMenu === i ? 'true' : 'false'}
                @click=${() => (this.chartMenu = this.chartMenu === i ? null : i)}
              >⋯</button>
              ${this.chartMenu === i
                ? html`<div class="argus-ch-pop" role="menu" @keydown=${(e: KeyboardEvent) => e.key === 'Escape' && (this.chartMenu = null)}>
                    <button type="button" role="menuitem" @click=${() => ((this.chartMenu = null), (this.chartDialog = { index: i }))}>Edit in Explore…</button>
                    <button type="button" role="menuitem" ?disabled=${i === 0} @click=${() => void store(moved(i, -1))}>Move up</button>
                    <button type="button" role="menuitem" ?disabled=${i === charts.length - 1} @click=${() => void store(moved(i, 1))}>Move down</button>
                    <div class="argus-ch-sep" role="separator"></div>
                    <button type="button" role="menuitem" class="is-danger" @click=${() => void store(charts.filter((_, k) => k !== i))}>Remove</button>
                  </div>`
                : nothing}
            </span>`
          : nothing}
      </li>`;
    };
    const dialog = this.chartDialog;
    return this.renderZone(
      'charts',
      'Charts',
      summary,
      html`${live
          ? nothing
          : html`<p class="argus-ch-save-first" role="note">
              <strong>Save the rule first.</strong> Then you can add and edit its charts.
            </p>`}
        <p class="argus-lfaint argus-e-zone-note">
          Shown as tabs on each alert's page and in every backtest of this rule, in this order. The alert sets the
          ship and the window. Applies on its own, straight away - not with Save. Not part of the version.
        </p>
        ${live
          ? html`<ul class="argus-ch-list">
                ${charts.map(row)}
                <li class="argus-ch-row is-guess">
                  ${chartIcon('line')}
                  <span class="argus-ch-text">
                    <span class="argus-ch-title">Argus's guess</span>
                    <span class="argus-ch-what">The series the rule reads, its numbers and its episodes</span>
                  </span>
                  <span class="argus-ch-badge">${charts.length ? 'Always last' : 'Opens first'}</span>
                </li>
              </ul>
              ${may && charts.length < MAX_CHARTS
                ? html`<button type="button" class="argus-ch-add" @click=${() => (this.chartDialog = { index: null })}>+ Add chart</button>`
                : nothing}
              ${this.chartsError ? html`<p class="argus-form-error" role="alert">${this.chartsError}</p>` : nothing}`
          : nothing}
        ${dialog && live
          ? html`<argus-chart-dialog
              .ruleId=${live.id}
              .ruleName=${live.name}
              .source=${live.source_key}
              .chart=${dialog.index === null ? null : (charts[dialog.index] ?? null)}
              @chart-dialog-close=${() => (this.chartDialog = null)}
              @chart-dialog-save=${(e: CustomEvent<ArgusRuleChart>) => {
                const next = [...charts];
                if (dialog.index === null) next.push(e.detail);
                else next[dialog.index] = e.detail;
                this.chartDialog = null;
                void store(next);
              }}
            ></argus-chart-dialog>`
          : nothing}`,
    );
  }

  /** Backtest, folded by default; its heading says what the last run found. */
  private renderBacktest(noun: string): TemplateResult {
    const ready = this.preview?.ok === true;
    return this.renderZone(
      'backtest',
      'Backtest',
      this.backtestSummary,
      html`<argus-backtest-panel
          wide
          .ruleId=${this.live()?.id ?? null}
          .ruleVersion=${this.live()?.version ?? null}
          .ruleName=${this.name.trim()}
          .document=${this.doc ? normalize(this.doc) : null}
          .noun=${noun}
          .ready=${ready}
          @argus-backtest-summary=${(e: CustomEvent<{ text: string; tone: string }>) => (this.backtestSummary = e.detail)}
        ></argus-backtest-panel>
        ${ready ? nothing : html`<p class="argus-lfaint">A backtest needs a whole rule - see Check for what is missing.</p>`}`,
    );
  }

  /**
   * Save, and stay: the editor keeps the rule open, now as saved, so the
   * next step - a backtest, the switch - is right here. The room is told,
   * so a new rule becomes that rule and the drawer and list agree.
   */
  private async save(): Promise<void> {
    // Save is always there to press. One press at a time; a press with
    // nothing changed sends nothing and says so, so pressing it again and
    // again costs nothing and never writes a version.
    if (!this.doc || this.saving) return;
    const before = this.live();
    this.clearError();
    this.savedNote = null;
    if (before && !this.docChanged() && !this.labelsChanged()) {
      this.savedNote = `Already saved. Still version ${before.version}.`;
      return;
    }
    if (!this.name.trim()) {
      this.fail('Give the rule a name first.');
      return;
    }
    if (before?.status === 'deployed' && this.docChanged()) {
      this.fail('This rule is running. Switch it off to save a change to the rule itself.');
      return;
    }
    this.saving = true;
    // What is saved is what was pressed on, and is checked first: a press
    // can come before the preview of the last change has answered.
    const doc = normalize(this.doc);
    window.clearTimeout(this.previewTimer);
    await this.refreshPreview();
    if (this.preview?.ok !== true) {
      this.saving = false;
      this.fail(this.preview?.error ?? 'The rule is not whole yet. See Check for what is missing.');
      return;
    }
    const answer = await saveRule(before?.id ?? null, this.name.trim(), doc, this.description.trim());
    this.saving = false;
    if (!answer.ok) {
      this.fail(answer.error);
      return;
    }
    const saved = rules.value.find((r) => r.id === answer.rule.id) ?? answer.rule;
    this.baseline = JSON.stringify(doc);
    this.name = saved.name;
    this.description = saved.description ?? '';
    this.savedNote = !before
      ? 'Saved as version 1.'
      : saved.version !== before.version
        ? `Saved as version ${saved.version}.`
        : `Saved. Still version ${saved.version}${saved.backtested ? ', and its backtest still counts' : ''}.`;
    this.dispatchEvent(new CustomEvent('argus-editor-saved', { bubbles: true, detail: { rule: saved } }));
  }

  /** Why something did not happen, as a toast that stays until closed: the
   *  editor is long, and a card at its end is below the fold. */
  private fail(message: string): void {
    this.clearError();
    this.errorToast = showToast('error', message, 0);
  }

  private clearError(): void {
    dismissToast(this.errorToast);
    this.errorToast = null;
  }

  private cancel(): void {
    this.dispatchEvent(new CustomEvent('argus-editor-close', { bubbles: true }));
  }

  /** On or off, from the editor: the same hold-to-deploy switch as the list.
   *  It acts on the saved version, never on unsaved blocks. */
  private async flip(on: boolean): Promise<void> {
    const live = this.live();
    if (!live) return;
    this.clearError();
    const error = await setStatus(live.id, on ? 'deployed' : 'draft');
    if (error) this.fail(error);
    else {
      const now = rules.value.find((r) => r.id === live.id);
      if (now) this.dispatchEvent(new CustomEvent('argus-editor-saved', { bubbles: true, detail: { rule: now } }));
    }
  }

  private renderSwitch(): TemplateResult | typeof nothing {
    const live = this.live();
    if (!live) return nothing;
    const on = live.status === 'deployed';
    const unsaved = this.docChanged();
    return html`<span class="argus-lswitch argus-e-switch ${on ? 'is-on' : ''}">
      <hold-switch
        .checked=${on}
        label=${`Deployed: ${live.name}`}
        hint-on=${live.backtested === false
          ? `Backtest version ${live.version} first`
          : unsaved
            ? `Hold to deploy version ${live.version}, as saved`
            : 'Hold to deploy'}
        hint-off="Hold to stop"
        @hold-commit=${(e: CustomEvent<{ checked: boolean }>) => void this.flip(e.detail.checked)}
      ></hold-switch>
      <span class="argus-e-switch-word">${on ? 'On' : 'Off'}</span>
    </span>`;
  }

  /* ── the heading: name and description ───────────────────
     As names are renamed elsewhere (a dashboard in the list, a Catalog card):
     text sized to itself, a pencil that shows only while that text is
     hovered, and a click on either opens it for typing. Enter or leaving the
     field keeps what was typed; Escape puts back what was there. */

  private openHead(which: 'name' | 'about'): void {
    this.headBefore = which === 'name' ? this.name : this.description;
    this.editingHead = which;
  }

  private headBefore = '';

  private closeHead(keep: boolean): void {
    if (keep) {
      this.savedNote = null;
      this.clearError();
    }
    if (!keep) {
      if (this.editingHead === 'name') this.name = this.headBefore;
      else if (this.editingHead === 'about') this.description = this.headBefore;
    }
    this.editingHead = null;
  }

  private renderName(): TemplateResult {
    if (this.editingHead === 'name') {
      return html`<input
        class="argus-e-name"
        type="text"
        placeholder="Name this rule"
        aria-label="Rule name"
        maxlength="140"
        .value=${this.name}
        ${autofocus()}
        @input=${(e: Event) => {
          this.name = (e.target as HTMLInputElement).value;
        }}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Enter') this.closeHead(true);
          if (e.key === 'Escape') this.closeHead(false);
        }}
        @blur=${() => this.closeHead(true)}
      />`;
    }
    const empty = !this.name.trim();
    return html`<span class="argus-e-line is-name">
      <button type="button" class="argus-e-text argus-e-name-text ${empty ? 'is-empty' : ''}" @click=${() => this.openHead('name')}>${empty ? 'Name this rule' : this.name}</button>
      <button type="button" class="argus-e-pencil" aria-label="Rename" title="Rename" @click=${() => this.openHead('name')}>✎</button>
    </span>`;
  }

  /** The data this rule watches, fixed once written. Said only when there is another. */
  private renderSource(key: string): TemplateResult | typeof nothing {
    if (sources.value.length < 2) return nothing;
    const name = sources.value.find((src) => src.key === key)?.name ?? key;
    return html`<p
      class="argus-e-source"
      title="A rule keeps its data source. EOS and Live name their signals differently and do not map to each other yet. To watch the other source, write a new rule."
    >
      Watches <b>${name}</b> data · fixed
    </p>`;
  }

  private renderAbout(): TemplateResult {
    if (this.editingHead === 'about') {
      return html`<textarea
        class="argus-e-about"
        rows="1"
        placeholder="What this rule is for"
        aria-label="Description"
        maxlength="2000"
        .value=${this.description}
        ${autofocus()}
        ${ref((el) => el && fitHeight(el as HTMLTextAreaElement))}
        @input=${(e: Event) => {
          const field = e.target as HTMLTextAreaElement;
          this.description = field.value;
          fitHeight(field);
        }}
        @keydown=${(e: KeyboardEvent) => {
          // Shift+Enter for a new line; Enter alone is done.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.closeHead(true);
          }
          if (e.key === 'Escape') this.closeHead(false);
        }}
        @blur=${() => this.closeHead(true)}
      ></textarea>`;
    }
    const empty = !this.description.trim();
    return html`<span class="argus-e-line is-about">
      <button type="button" class="argus-e-text argus-e-about-text ${empty ? 'is-empty' : ''}" @click=${() => this.openHead('about')}>${empty ? 'Add a description' : this.description}</button>
      <button type="button" class="argus-e-pencil" aria-label="Edit the description" title="Edit the description" @click=${() => this.openHead('about')}>✎</button>
    </span>`;
  }

  /** Backtest: the blocks, and what they read as under them. */
  private renderQuick(doc: ArgusTreeDocument): TemplateResult {
    return html`
      <div class="argus-e is-quick" @click=${(e: Event) => (this.readonly ? undefined : this.onClick(e))}>
        <div class="argus-b-canvas ${this.readonly ? 'is-readonly' : ''}" ?inert=${this.readonly}>
          <div class="argus-b-stack">
            ${clause('watch', 'def', this.renderWatch(doc), true)}
            ${clause('alert', 'logic', this.renderAlert(doc), true, doc.shape.kind === 'silence' ? nothing : logicKey(doc.condition))}
            ${clause('timing', 'time', this.renderTiming(doc), true)}
          </div>
          <div class="argus-b-stack">${clause('signals', 'signal', this.renderSignals(doc))}</div>
          <div class="argus-b-stack">${clause('where', 'def', this.renderWhere(doc))}</div>
        </div>
        ${this.renderAssistantChange()} ${this.renderCheck()}
        ${this.renderPop()}
      </div>
    `;
  }

  override render(): TemplateResult {
    const doc = this.doc;
    if (!doc) return html`<p class="argus-room-waiting">Opening the editor…</p>`;
    if (this.quick) return this.renderQuick(doc);
    const live = this.live();
    const newVersion = this.docChanged();
    const dirty = !live || newVersion || this.labelsChanged();
    // Decision 28: a running rule is not edited. A change to the rule itself
    // can still be looked at and backtested here; saving it waits until the
    // rule is stopped. Its name and description are labels, saved any time.
    const blocked = live?.status === 'deployed' && newVersion;
    return html`
      <div class="argus-e" @click=${(e: Event) => this.onClick(e)}>
        <div class="argus-e-head">
          <div class="argus-e-titles">${this.renderName()} ${this.renderAbout()} ${this.renderSource(doc.source)}</div>
          <div class="argus-e-actions">
            ${this.renderSwitch()}
            <button type="button" class="dash-btn" @click=${() => this.cancel()}>${dirty ? 'Cancel' : 'Close'}</button>
            <button
              type="button"
              class="btn btn-primary"
              ?disabled=${this.saving}
              title=${blocked
                ? 'Switch the rule off to save a change to the rule itself.'
                : !dirty
                  ? 'Nothing has changed since it was saved. Saving keeps the version.'
                  : live && !newVersion
                    ? 'A new name or description keeps the version, so its backtest still counts.'
                    : ''}
              @click=${() => void this.save()}
            >
              ${this.saving ? 'Saving…' : !live ? 'Save rule' : newVersion ? 'Save as a new version' : 'Save'}
            </button>
          </div>
        </div>
        ${this.savedNote ? html`<p class="argus-e-saved" role="status">${this.savedNote}</p>` : nothing}
        ${this.renderAssistantChange()}
        ${blocked
          ? html`<p
              class="argus-e-running"
              role="note"
              title="This rule is running, so a change to the rule itself cannot be saved yet. You can check and backtest it here. To save it, switch the rule off, save, backtest the new version, and switch it on again. A new name or description can be saved while it runs."
            >
              <b>This rule is running,</b> so a change to the rule itself cannot be saved yet. You can
              check and backtest it here. To save it, switch the rule off, save, backtest the new
              version, and switch it on again. A new name or description can be saved while it runs.
            </p>`
          : nothing}
        ${this.renderRuleZone(doc)}
        ${this.renderBacktest(this.scopeInfo?.entity_noun ?? 'entity')}
        ${this.renderCharts()}
        ${this.renderSchedule()}
        ${this.renderPop()}
      </div>
    `;
  }
}

/** As many charts as a rule keeps (`argus_charts.MAX_CHARTS`). */
const MAX_CHARTS = 8;

/** A small picture of a plot type, for a chart's row. */
function chartIcon(type: string): TemplateResult {
  const shapes: Record<string, SVGTemplateResult> = {
    line: svg`<path d="M2 13 L7 6 L11 10 L15 4 L20 8" fill="none" stroke="currentColor" stroke-width="1.6" />`,
    scatter: svg`<g fill="currentColor"><circle cx="5" cy="11" r="1.5" /><circle cx="9" cy="8" r="1.5" /><circle cx="13" cy="9" r="1.5" /><circle cx="16" cy="5" r="1.5" /><circle cx="7" cy="13" r="1.5" /></g>`,
    gauge: svg`<path d="M3 14 A8 8 0 0 1 19 14" fill="none" stroke="currentColor" stroke-width="1.6" /><path d="M11 14 L15 8" stroke="currentColor" stroke-width="1.6" />`,
    table: svg`<g fill="none" stroke="currentColor" stroke-width="1.3"><rect x="2.5" y="2.5" width="17" height="12" /><path d="M2.5 6.5 H19.5 M2.5 10.5 H19.5 M9 2.5 V14.5" /></g>`,
    map: svg`<path d="M3 13 C6 4 10 14 13 7 S18 5 19 3" fill="none" stroke="currentColor" stroke-width="1.6" /><circle cx="19" cy="3" r="1.6" fill="currentColor" />`,
  };
  const bars = svg`<g fill="currentColor"><rect x="3" y="9" width="3" height="6" /><rect x="7" y="4" width="3" height="11" /><rect x="11" y="6" width="3" height="9" /><rect x="15" y="10" width="3" height="5" /></g>`;
  return html`<svg class="argus-ch-type" viewBox="0 0 22 17" aria-hidden="true">${shapes[type] ?? bars}</svg>`;
}

/** What a chart plots, in one line, from its recipe. */
export function chartWhat(query: string): string {
  const p = new URLSearchParams(query);
  const type = p.get('pt') || 'line';
  const yl = (p.get('yl') || '').split(',').filter(Boolean);
  const yr = (p.get('yr') || '').split(',').filter(Boolean);
  const x = p.get('x');
  const ys = [...yl, ...yr.map((y) => `${y} (right)`)].join(' · ');
  if ((type === 'line' || type === 'scatter') && ys) return x && type === 'scatter' ? `${x} × ${ys}` : ys;
  return type.charAt(0).toUpperCase() + type.slice(1);
}

/** A description field as tall as what is written in it, one line when empty. */
function fitHeight(field: HTMLTextAreaElement): void {
  field.style.height = 'auto';
  field.style.height = `${field.scrollHeight}px`;
}

/** Focus a field once, when a click has just opened it - not on every redraw. */
class AutofocusDirective extends Directive {
  private done = false;

  constructor(part: PartInfo) {
    super(part);
    if (part.type !== PartType.ELEMENT) throw new Error('autofocus goes on an element');
  }

  override update(part: ElementPart): typeof nothing {
    if (this.done) return nothing;
    this.done = true;
    const el = part.element as HTMLInputElement;
    queueMicrotask(() => {
      el.focus();
      if (el.type !== 'search') el.select?.();
    });
    return nothing;
  }
  render(): typeof nothing {
    return nothing;
  }
}

const autofocus = directive(AutofocusDirective);

declare global {
  interface HTMLElementTagNameMap {
    'argus-rule-editor': ArgusRuleEditor;
  }
}
